// ============================================================
// WorkspaceAgentCard — a governed collaborator, not a chatbot.
//
// The builder-card grammar from the QUAM command center: status
// dot (live state), agent name in its accent, role line, skill
// chips, a suggestion/next-action body, and governed action
// buttons. Used by Agent Workspace, Agent Control Center, and
// Meeting Mode.
//
// Honesty contract: `state` must reflect the registry truth
// (active / paused / waiting approval). A paused agent renders
// paused — the card never fakes activity.
// ============================================================

import { WorkspaceRailChips } from './WorkspaceRightRail'
import WorkspaceStatusChip from './WorkspaceStatusChip'
import {
  tint,
  WORKSPACE_ACCENTS,
  WORKSPACE_FONT,
  WORKSPACE_SURFACE,
  WORKSPACE_TEXT,
} from './workspaceTheme'

export function WorkspaceThinkingDots({ accent = WORKSPACE_ACCENTS.cyan, size = 4 }) {
  return (
    <span aria-label="working" style={{ display: 'inline-flex', gap: 2 }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: accent,
            animation: `sc-think-pulse 1.2s ease ${i * 0.2}s infinite`,
          }}
        />
      ))}
    </span>
  )
}

export default function WorkspaceAgentCard({
  name,
  role,
  accent = WORKSPACE_ACCENTS.purple,
  state = 'idle',      // active | paused | waiting | approval_required | idle | …
  stateLabel,
  thinking = false,
  summary,             // suggestion / last report / next action
  chips,               // skills, lanes — ['skill'] or [{ label, accent }]
  evidence,            // small mono footnote, e.g. 'evidence: registry · audited'
  actions,             // ReactNode — governed controls (Open, Pause, Approve…)
  onOpen,
  compact = false,
  style: styleOverride,
}) {
  const stateMetaAccent =
    state === 'active' ? WORKSPACE_ACCENTS.green
    : state === 'paused' || state === 'waiting' ? WORKSPACE_ACCENTS.gold
    : String(state).startsWith('approval') ? WORKSPACE_ACCENTS.purple
    : 'rgba(255,255,255,0.25)'

  return (
    <article
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 6 : 8,
        padding: compact ? '10px 12px' : '14px 16px',
        borderRadius: 'var(--sc-radius-md, 10px)',
        background: tint(accent, 5),
        border: `1px solid ${tint(accent, 12)}`,
        minWidth: 0,
        ...styleOverride,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {thinking ? (
          <WorkspaceThinkingDots accent={accent} />
        ) : (
          <span
            aria-hidden
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              flexShrink: 0,
              background: stateMetaAccent,
              boxShadow: state === 'active' ? `0 0 8px ${tint(WORKSPACE_ACCENTS.green, 55)}` : 'none',
            }}
          />
        )}
        <span
          style={{
            fontSize: compact ? 13 : 14,
            fontWeight: 800,
            color: accent,
            fontFamily: WORKSPACE_FONT.mono,
            letterSpacing: '0.02em',
          }}
        >
          {name}
        </span>
        {role && (
          <span
            style={{
              fontSize: 11,
              color: WORKSPACE_TEXT.tertiary,
              fontFamily: WORKSPACE_FONT.ui,
            }}
          >
            {role}
          </span>
        )}
        <span style={{ marginLeft: 'auto' }}>
          <WorkspaceStatusChip status={state} label={stateLabel} size="sm" />
        </span>
      </div>

      {summary && (
        <div
          style={{
            fontSize: compact ? 12 : 12.5,
            lineHeight: 1.7,
            color: WORKSPACE_TEXT.secondary,
            fontFamily: WORKSPACE_FONT.ui,
          }}
        >
          {summary}
        </div>
      )}

      {chips && chips.length > 0 && <WorkspaceRailChips items={chips} />}

      {(evidence || actions || onOpen) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginTop: 2,
          }}
        >
          <span
            style={{
              fontSize: 9,
              color: WORKSPACE_TEXT.muted,
              fontFamily: WORKSPACE_FONT.mono,
              letterSpacing: '0.06em',
            }}
          >
            {evidence}
          </span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {actions}
            {onOpen && (
              <button
                type="button"
                onClick={onOpen}
                style={{
                  padding: '4px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: tint(accent, 10),
                  border: `1px solid ${tint(accent, 20)}`,
                  color: accent,
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: WORKSPACE_FONT.mono,
                  letterSpacing: '0.06em',
                }}
              >
                OPEN →
              </button>
            )}
          </div>
        </div>
      )}
    </article>
  )
}
